import type { ComboboxOption } from '@sparklayer-io/spark-dash-ui/components/ui/combobox/types';
import { Combobox } from '@ui/themed';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { CUSTOMER_ROLE_TYPE } from '../../customers/constants';
import { useSearchCustomers } from '../../customers/hooks/use-search-customers';
import type { CustomerSearchQuery } from '../../customers/types/api';
import type { CustomerMinimal } from '../types/company';

const MIN_SEARCH_LENGTH = 2;

/**
 * Name and email are folded into the one option label because `ComboboxOption.label` is a single
 * string. The email is what tells two customers with the same name apart, so it has to be here.
 */
function customerOption(customer: {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}): ComboboxOption {
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  const label = name && customer.email ? `${name} (${customer.email})` : name || customer.email;

  return { value: customer.id, label: label || customer.id };
}

type CommonProps = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: string;
  onChange: (id: string) => void;
  /** Pre-selected customer so its label shows without a lookup (edit mode). */
  seed?: CustomerMinimal | null;
};

type MultiProps = CommonProps & {
  multiple: true;
  value: string[];
  onChange: (ids: string[]) => void;
  seed?: CustomerMinimal[];
};

type CustomerSearchSelectProps = SingleProps | MultiProps;

/** A searchable customer picker (single or multi) backed by the customer search API. */
export function CustomerSearchSelect(props: CustomerSearchSelectProps) {
  const [term, setTerm] = useState('');

  const query = useMemo<CustomerSearchQuery>(
    () =>
      term.length >= MIN_SEARCH_LENGTH
        ? { role_type: CUSTOMER_ROLE_TYPE.CUSTOMER, search_term: term }
        : { role_type: CUSTOMER_ROLE_TYPE.CUSTOMER },
    [term],
  );

  const { data: results, isFetching } = useSearchCustomers(query);

  // Seeds lead so an edit-mode selection has a label on first paint, before any search runs.
  // `Combobox` remembers every option it is handed, so they stay resolvable afterwards.
  const options = useMemo<ComboboxOption[]>(() => {
    const seeds = props.multiple ? (props.seed ?? []) : props.seed ? [props.seed] : [];
    const byValue = new Map<string, ComboboxOption>();

    for (const customer of [...seeds, ...(results ?? [])]) {
      const option = customerOption(customer);
      byValue.set(option.value, option);
    }

    return [...byValue.values()];
  }, [props.seed, props.multiple, results]);

  const label = props.label && props.required ? `${props.label} *` : props.label;
  const placeholder =
    props.placeholder ??
    t('features:companies.components.customer_search_select.search_placeholder');
  const noOptionsMessage = t('features:companies.components.customer_search_select.no_options');

  if (props.multiple) {
    return (
      <Combobox
        multiple
        label={label}
        placeholder={placeholder}
        error={props.error}
        options={options}
        value={props.value}
        onChange={(ids) => props.onChange(ids)}
        onSearch={setTerm}
        loading={isFetching}
        noOptionsMessage={noOptionsMessage}
      />
    );
  }

  return (
    <Combobox
      clearable
      label={label}
      placeholder={placeholder}
      error={props.error}
      options={options}
      value={props.value || null}
      onChange={(id) => props.onChange(id ?? '')}
      onSearch={setTerm}
      loading={isFetching}
      noOptionsMessage={noOptionsMessage}
    />
  );
}
